import type { ProductCardDto } from '@telestore/contracts';
import { pickText } from '@telestore/contracts';
import type { Locale } from '@telestore/i18n';
import { type LayoutConfig, ratioToCss } from '@telestore/theming';
import { Link } from '@tanstack/react-router';
import { Badge, ComparePrice, Money, ProductImage } from './primitives.js';

/**
 * Layout variants (CLAUDE.md §5).
 *
 * These switch COMPONENTS, not CSS. Every variant of the slot shares one props
 * contract, which is what keeps the visual test matrix finite and what makes
 * "no drag-drop page builder, ever" a workable position.
 */

export interface ProductCardProps {
  product: ProductCardDto;
  shopSlug: string;
  currency: 'USD' | 'KHR';
  locale: Locale;
  layout: LayoutConfig;
  priority?: boolean;
  outOfStockLabel: string;
  lowStockLabel: (count: number) => string;
}

function GridCard(props: ProductCardProps) {
  const { product, shopSlug, currency, locale, layout, priority } = props;
  /**
   * Two ways to be unavailable (§4). Retail counts stock to zero; F&B flips a
   * flag, because a dish that has run out has no stock number to fall.
   */
  const soldOut = product.soldOut || (product.totalStock !== null && product.totalStock <= 0);
  const low = product.totalStock !== null && product.totalStock > 0 && product.totalStock <= 3;

  return (
    <Link
      to="/s/$slug/p/$productSlug"
      params={{ slug: shopSlug, productSlug: product.slug }}
      className="group flex flex-col gap-2"
    >
      <div className="relative">
        <ProductImage
          url={product.images[0]?.url ?? null}
          alt={pickText(product.name, locale)}
          ratio={ratioToCss(layout.card_image_ratio)}
          {...(priority ? { priority: true } : {})}
        />
        {soldOut ? (
          <div
            className="absolute inset-0 flex items-center justify-center rounded-[var(--radius-card)] text-sm font-semibold"
            style={{ background: 'var(--color-overlay)', color: '#fff' }}
          >
            {props.outOfStockLabel}
          </div>
        ) : null}
      </div>
      <p className="line-clamp-2 text-sm font-medium">{pickText(product.name, locale)}</p>
      <div className="flex flex-wrap items-baseline gap-2">
        <Money amount={product.price.amount} currency={currency} className="text-sm font-semibold" />
        {layout.card_show.includes('compare_at') && product.compareAtPrice ? (
          <ComparePrice amount={product.compareAtPrice.amount} currency={currency} />
        ) : null}
      </div>
      {low ? (
        <span className="text-xs" style={{ color: 'var(--color-accent)' }}>
          {props.lowStockLabel(product.totalStock!)}
        </span>
      ) : null}
    </Link>
  );
}

function ListCard(props: ProductCardProps) {
  const { product, shopSlug, currency, locale, layout } = props;
  /**
   * Two ways to be unavailable (§4). Retail counts stock to zero; F&B flips a
   * flag, because a dish that has run out has no stock number to fall.
   */
  const soldOut = product.soldOut || (product.totalStock !== null && product.totalStock <= 0);

  return (
    <Link
      to="/s/$slug/p/$productSlug"
      params={{ slug: shopSlug, productSlug: product.slug }}
      className="flex items-center gap-3 py-3"
      style={{ borderBottom: '1px solid var(--color-border)' }}
    >
      <div className="w-20 shrink-0">
        <ProductImage
          url={product.images[0]?.url ?? null}
          alt={pickText(product.name, locale)}
          ratio={ratioToCss(layout.card_image_ratio)}
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="line-clamp-2 text-sm font-medium">{pickText(product.name, locale)}</p>
        <div className="flex items-baseline gap-2">
          <Money amount={product.price.amount} currency={currency} className="text-sm font-semibold" />
          {product.compareAtPrice ? (
            <ComparePrice amount={product.compareAtPrice.amount} currency={currency} />
          ) : null}
        </div>
        {soldOut ? <Badge tone="warn">{props.outOfStockLabel}</Badge> : null}
      </div>
    </Link>
  );
}

function TextRow(props: ProductCardProps) {
  const { product, shopSlug, currency, locale } = props;
  return (
    <Link
      to="/s/$slug/p/$productSlug"
      params={{ slug: shopSlug, productSlug: product.slug }}
      className="flex items-center justify-between gap-4 py-3"
      style={{ borderBottom: '1px solid var(--color-border)' }}
    >
      <span className="min-w-0 flex-1 truncate text-sm font-medium">
        {pickText(product.name, locale)}
      </span>
      <Money amount={product.price.amount} currency={currency} className="text-sm font-semibold" />
    </Link>
  );
}

/** The one switch over the layout enum. Adding a variant means adding a case. */
export function ProductCard(props: ProductCardProps) {
  switch (props.layout.product_list) {
    case 'list':
      return <ListCard {...props} />;
    case 'list-text':
      return <TextRow {...props} />;
    case 'grid-2':
    case 'grid-3':
      return <GridCard {...props} />;
  }
}

export function ProductList({
  products,
  ...rest
}: Omit<ProductCardProps, 'product' | 'priority'> & { products: ProductCardDto[] }) {
  const layout = rest.layout;

  if (layout.product_list === 'list' || layout.product_list === 'list-text') {
    return (
      <div className="flex flex-col px-4">
        {products.map((product) => (
          <ProductCard key={product.id} product={product} {...rest} />
        ))}
      </div>
    );
  }

  const columns = layout.product_list === 'grid-3' ? 'grid-cols-3' : 'grid-cols-2';
  return (
    <div className={`grid ${columns} gap-x-3 gap-y-6 px-4`}>
      {products.map((product, index) => (
        <ProductCard key={product.id} product={product} priority={index < 4} {...rest} />
      ))}
    </div>
  );
}
